import * as CarouselElements from 'react-aria-carousel';
import styled from 'styled-components';

// Structural wrapper that carries the ref used for the GA4 `view_promotion`
// in-view impression (react-aria-carousel's Carousel doesn't forward a DOM ref).
export const Root = styled.div`
  width: 100%;
`;

// Flanking-arrow carousel matching the N-FP-10 mock: a relative shell, a snap-scroll
// track inset to leave room for the arrows, and two outlined-white circular arrows.
export const Carousel = styled(CarouselElements.Carousel)`
  position: relative;
  width: 100%;
`;

export const Track = styled(CarouselElements.CarouselScroller)<{
  $scroll?: boolean;
}>`
  display: grid;
  grid-auto-flow: column;
  justify-content: ${({ $scroll }) => ($scroll ? 'start' : 'center')};
  gap: 12px;
  margin-inline: 44px;
  padding-block: 5px;
  overflow: auto;
  scroll-snap-type: x mandatory;
  scrollbar-width: none;
  &::-webkit-scrollbar { display: none; }
  & [data-carousel-item] > * { width: 100%; height: 100%; }
  &:focus-visible {
    box-shadow: var(--shadow-focus);
    outline: 0;
  }

  @media (width < 64rem) {
    margin-inline: 0;
  }
`;

// --- Brand card (ported from the mock .nfp10-card) ---
const cardBase = `
  display: flex;
  flex-direction: column;
  border-radius: var(--radius-xlarge, 12px);
  padding-bottom: 12px;
  overflow: hidden;
  box-shadow: var(--shadow-card, 0 4px 16px rgb(10 13 18 / 7%), 0 1px 3px rgb(10 13 18 / 6%));
  text-decoration: none;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
  &:hover { transform: translateY(-2px); }
`;

export const CardLink = styled.a`
  ${cardBase}
`;

export const CardStatic = styled.div`
  ${cardBase}
`;

export const ImageBox = styled.div`
  width: 100%;
  aspect-ratio: 1 / 1;
  background: var(--grey-100);
  overflow: hidden;
`;

export const Img = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

/**
 * #2928 - slides can now take a dark promotional background. The colour classes in global.css
 * set `color: white`, but the styled rules below are more specific and would win, leaving dark
 * text on a dark slide. Each element that hardcodes a colour therefore restates it for a dark
 * surface. surface-moss-green is included so the existing dark green behaves consistently.
 */
const onDarkSurface =
  ':is(.surface-deep-red, .surface-black, .surface-christmas-green, .surface-brand-green, .surface-moss-green) &';

export const Label = styled.div`
  margin: 12px 11px 4px;
  font-size: var(--font-size-text-sm, 0.875rem);
  font-weight: var(--font-weight-bold);
  line-height: 1.25;
  color: var(--black);

  ${onDarkSurface} { color: var(--white); }
`;

export const LinkRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 4px;
  margin-inline: 11px;
  font-size: var(--font-size-text-xs, 0.75rem);
  font-weight: var(--font-weight-medium);
  color: var(--grey-700);
  & svg { flex: 0 0 13px; width: 13px; height: 13px; color: var(--grey-500); }

  ${onDarkSurface} {
    color: var(--white);
    & svg { color: var(--white); }
  }
`;

export const CtaButton = styled.a`
  margin: 8px 11px 0;
  padding: 8px 12px;
  border-radius: var(--radius-large);
  background: var(--brand-500);
  border: 1px solid var(--brand-500);
  color: var(--black);
  font-size: var(--font-size-text-sm, 0.875rem);
  font-weight: var(--font-weight-semibold);
  text-align: center;
  text-decoration: none;
  &:hover { background: var(--brand-600); border-color: var(--brand-600); }
  &:focus-visible { box-shadow: var(--shadow-focus); outline: 0; }
`;
